import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

/**
 * Primitivas finas sobre as classes do legado (componentes.css / layout.css).
 * Sem CSS próprio de chrome: card, kpi-card, tabs-pill, tbl, badge-status, alert, empty-state…
 * Só layout auxiliar vive em `styles/legado-react.css` (prefixo `hi-`).
 */

type PageHeaderProps = {
  title: ReactNode
  subtitle?: ReactNode
  /** Link "voltar" / trilha acima do título. */
  backTo?: { to: string; label: string }
  actions?: ReactNode
}

/** Cabeçalho de módulo: `.section-header` + `.card-subtitle` (padrão do legado). */
export function PageHeader({ title, subtitle, backTo, actions }: PageHeaderProps) {
  return (
    <header className="section-header hi-page-header">
      <div className="hi-page-header-text">
        {backTo ? (
          <Link className="hi-crumb" to={backTo.to}>
            ‹ {backTo.label}
          </Link>
        ) : null}
        <h3>{title}</h3>
        {subtitle ? <p className="card-subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="hi-actions">{actions}</div> : null}
    </header>
  )
}

export function KpiGrid({ children }: { children: ReactNode }) {
  return <div className="kpi-grid">{children}</div>
}

export function Kpi({
  label,
  value,
  className,
}: {
  label: string
  value: ReactNode
  /** Classes extras no `.kpi-card` (ex.: acento de módulo). */
  className?: string
}) {
  return (
    <div className={className ? `kpi-card ${className}` : 'kpi-card'}>
      <div className="kpi-value">{value}</div>
      <div className="kpi-label" title={label}>
        {label}
      </div>
    </div>
  )
}

type Tone = 'danger' | 'success' | 'info' | 'warning'

export function Alert({
  tone = 'info',
  children,
  role,
}: {
  tone?: Tone
  children: ReactNode
  role?: 'alert' | 'status'
}) {
  return (
    <div className={`alert alert-${tone}`} role={role ?? (tone === 'danger' ? 'alert' : 'status')}>
      <div className="alert-body">{children}</div>
    </div>
  )
}

export type TabItem<T extends string> = { id: T; label: string }

export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: readonly TabItem<T>[]
  value: T
  onChange: (id: T) => void
  label: string
}) {
  return (
    <nav className="tabs-pill" aria-label={label}>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className={`tab${value === item.id ? ' active' : ''}`}
          onClick={() => onChange(item.id)}
        >
          {item.label}
        </button>
      ))}
    </nav>
  )
}

/** Card padrão com título (card / card-header / card-title / card-body). */
export function Section({
  title,
  actions,
  children,
  flush,
}: {
  title?: ReactNode
  actions?: ReactNode
  children: ReactNode
  /** Sem padding no corpo (tabelas). */
  flush?: boolean
}) {
  return (
    <section className="card">
      {title || actions ? (
        <div className="card-header">
          <h4 className="card-title">{title}</h4>
          {actions}
        </div>
      ) : null}
      <div className={flush ? undefined : 'card-body'}>{children}</div>
    </section>
  )
}

/** Tabela no padrão `.tbl-wrapper > .table-wrap > table.tbl`. */
export function DataTable({ children }: { children: ReactNode }) {
  return (
    <div className="tbl-wrapper">
      <div className="table-wrap">
        <table className="tbl">{children}</table>
      </div>
    </div>
  )
}

export type StatusTone = 'ativo' | 'pendente' | 'inativo'

/** Pílula de status do legado (`.badge-status.ativo|pendente|inativo`). */
export function StatusBadge({ tone, children }: { tone: StatusTone; children: ReactNode }) {
  return <span className={`badge-status ${tone}`}>{children}</span>
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="hi-empty">{children}</p>
}

export function Muted({ children }: { children: ReactNode }) {
  return <p className="hi-muted">{children}</p>
}

export function Avatar({
  src,
  nome,
  iniciais,
  size = 'md',
}: {
  src?: string | null
  nome?: string | null
  iniciais?: string | null
  size?: 'sm' | 'md' | 'lg'
}) {
  const cls = `hi-avatar hi-avatar--${size}`
  if (src) return <img className={cls} src={src} alt="" />
  const label =
    iniciais ||
    (nome || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase() ||
    'HI'
  return (
    <span className={cls} aria-hidden="true">
      {label}
    </span>
  )
}
